import React, { useState, useEffect, useContext } from "react";
import { 
  Box, Typography, Grid, MenuItem, FormControl,
  Select, InputLabel, CircularProgress, Button, IconButton,
  Divider, FormHelperText, Tooltip, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControlLabel, Checkbox, 
  Popover, TextField, Radio, RadioGroup, FormLabel, FormGroup
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SortIcon from '@mui/icons-material/Sort';

import darkTheme from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon } from "../../components/common/opus";
import ActivityTimeline from "../../components/common/ActivityTimeline";
import { useAlert } from "../../contexts/AlertContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useSession } from "../../contexts/SessionContext";
import apiService from '../../services/api.service';


const ActivityLogs = () => {
  const alert = useAlert();
  const { logout, userInfo } = useContext(AuthContext);
  const { selectedOrgId: sessionOrgId } = useSession(); // OPUS-913: follow global sidebar org

  const userRole = userInfo?.data?.role?.name;
  const isSuperAdmin = userRole === "super_admin";
  
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filters, setFilters] = useState({
    activityTypes: {
      VIEW: false,
      CREATE: true,
      UPDATE: true,
      DELETE: true,
      LOGIN: true,
      LOGOUT: true,
      UPLOAD: true,
      RENDERER_ACTION: true
    },
    sortBy: 'newest'
  });
  
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedOrgName, setSelectedOrgName] = useState("");
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleDateDialogOpen = () => {
    setDateDialogOpen(true);
  };
  
  const handleDateDialogClose = () => {
    setDateDialogOpen(false);
  };
  
  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      activityTypes: {
        ...filters.activityTypes,
        [event.target.name]: event.target.checked
      }
    });
  };
  
  const handleSortChange = (event) => {
    setFilters({
      ...filters,
      sortBy: event.target.value
    });
  };
  
  const applyFilters = () => {
    handleFilterClose();
    setPage(1);
    setActivities([]);
    fetchActivities(selectedOrg, 1);
  };
  
  const applyDateRange = () => {
    handleDateDialogClose();
    setPage(1);
    setActivities([]);
    fetchActivities(selectedOrg, 1);
  };
  
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // OPUS-913: the org now comes from the global sidebar selector.
  useEffect(() => {
    if (sessionOrgId && sessionOrgId !== selectedOrg) setSelectedOrg(sessionOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionOrgId]);

  useEffect(() => {
    if (selectedOrg) {
      setPage(1);
      setActivities([]);
      fetchActivities(selectedOrg, 1);
    }
  }, [selectedOrg]);
  
  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/common/organizations`, {
      }); 
      if (!response.ok) {
        throw new Error("Failed to fetch organizations");
      }
      
      const data = await response.json();
      if (data) {
        let orgs = [];
        if (Array.isArray(data)) {
          orgs = data.filter(org => org.status === 'active');
        } else if (Array.isArray(data.organizations)) {
          orgs = data.filter(org => org.status === 'active');
        } 
        setOrganizations(orgs);
        if (orgs.length > 0) {
          if (isSuperAdmin) {
            setSelectedOrg(orgs[0]._id);
            setSelectedOrgName(orgs[0].name);
          } else {
            const userOrg = orgs.find(org => org._id === userInfo?.data?.organization_id);
            if (userOrg) {
              setSelectedOrg(userOrg._id);
              setSelectedOrgName(userOrg.name);
            } else if (orgs.length > 0) {
              setSelectedOrg(orgs[0]._id);
              setSelectedOrgName(orgs[0].name);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
      alert.error("Failed to load organizations. Please try again later.");
    } finally {
      setLoadingOrgs(false);
    }
  };
  
  const fetchActivities = async (orgId, pageNum, append = false) => {
    if (!orgId) return;

    // OPUS-913: append (load-more) must not flip `loading` — that unmounts the
    // timeline and resets the scroll to the top. Use loadingMore for appends.
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    
    try {
      let queryParams = `page=${pageNum}&limit=30`;
      
      if (filters.activityTypes) {
        const activeTypes = Object.keys(filters.activityTypes).filter(
          type => filters.activityTypes[type]
        );
        if (activeTypes.length > 0) {
          queryParams += `&activityTypes=${activeTypes.join(',')}`;
        }
      }
      
      if (startDate && endDate) {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        queryParams += `&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      }
      
      queryParams += `&sort=${filters.sortBy === 'newest' ? '-timestamp' : 'timestamp'}`;
      
      const params = new URLSearchParams(queryParams);
      const data = await apiService.getActivityLogs(orgId, Object.fromEntries(params));
      
      const result = data.data || data;

      const activities = result.activities || [];
      const pagination = result.pagination || {};

      if (filters.sortBy === 'oldest' && pageNum === 1 && !append) {
        activities.reverse();
      }

      // OPUS-913: derive hasMore robustly — use pagination.pages when present,
      // otherwise fall back to "got a full page" (server may omit page metadata).
      const curPage = pagination.page || pageNum || 1;
      const totalPages = pagination.pages || pagination.totalPages;
      const more = totalPages ? curPage < totalPages : activities.length >= 30;
      setTotalActivities(pagination.total || 0);
      setHasMore(more);
      setPage(curPage);
      
      if (append) {
        setActivities(prev => [...prev, ...activities]);
      } else {
        setActivities(activities);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      setError("Failed to load activity data. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const handleOrgChange = (event) => {
    const orgId = event.target.value;
    setSelectedOrg(orgId);
    
    const org = organizations.find(o => o._id === orgId);
    setSelectedOrgName(org ? org.name : "");
  };
  
  const handleRefresh = () => {
    if (selectedOrg) {
      setActivities([]);
      setPage(1);
      fetchActivities(selectedOrg, 1);
    }
  };
  
  const handleLoadMore = () => {
    if (hasMore && !loadingMore && selectedOrg) {
      fetchActivities(selectedOrg, page + 1, true);
    }
  };

  const hasActiveFilters = () => {
    if (startDate || endDate) return true;
    
    const defaultTypes = Object.values(filters.activityTypes).every(v => v === true);
    return !defaultTypes || filters.sortBy !== 'newest';
  };

  const clearAllFilters = () => {
    setFilters({
      activityTypes: {
        VIEW: true,
        CREATE: true,
        UPDATE: true,
        DELETE: true,
        LOGIN: true,
        LOGOUT: true,
        UPLOAD: true,
        RENDERER_ACTION: true
      },
      sortBy: 'newest'
    });
    setStartDate(null);
    setEndDate(null);
    if (selectedOrg) {
      setActivities([]);
      setPage(1);
      fetchActivities(selectedOrg, 1);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid container spacing={1} sx={{ height: '100vh' }}>
        <Grid size={12}>
          <DashboardBreadcrumbs />
        </Grid>

        <Grid size={12} sx={{ height: '100%' }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              // OPUS-1045: offset on the surface (not the grid cell) so the
              // background position matches the canonical page wrapper.
              height: "calc(100% - 67px)",
              p: 2,
              background: "var(--surface-1)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-lg)",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
              {/* OPUS-913: organisation selection moved to the global sidebar selector */}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Filter & sort activities">
                  <IconButton 
                    onClick={handleFilterClick} 
                    color={
                      Object.values(filters.activityTypes).some(v => !v) || 
                      filters.sortBy !== 'newest' ? 'primary' : 'default'
                    }
                  >
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Select date range">
                  <IconButton 
                    onClick={handleDateDialogOpen} 
                    color={startDate && endDate ? 'primary' : 'default'}
                  >
                    <DateRangeIcon />
                  </IconButton>
                </Tooltip>
                
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  startIcon={<OpusIcon name="refresh" size={18} />}
                  onClick={handleRefresh}
                  disabled={loading || !selectedOrg}
                  sx={{ ml: 0.5 }}
                >
                  Refresh
                </Button>
                
                {hasActiveFilters() && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="warning"
                    onClick={clearAllFilters}
                    sx={{ ml: 0.5 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
              
              {startDate && endDate && (
                <Tooltip title={`Date Range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      px: 1.5, 
                      py: 0.5, 
                      ml: 2,
                      borderRadius: 1,
                      bgcolor: 'primary.dark',
                      border: 1,
                      borderColor: 'primary.main'
                    }}
                  >
                    <DateRangeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="caption">Date Filter Active</Typography>
                  </Box>
                </Tooltip>
              )}
              
              <Box sx={{ flexGrow: 1 }} />
              
              {activities.length > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {totalActivities.toLocaleString()} activities
                </Typography>
              )}
            </Box>
            
            {/* Activity Timeline — the timeline owns its own scroll (infinite-load
                listener lives there); this wrapper must NOT also scroll. */}
            <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexGrow: 1 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ textAlign: 'center', py: 4, flexGrow: 1 }}>
                  <Typography color="error">{error}</Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 2 }}
                    onClick={handleRefresh}
                  >
                    Retry
                  </Button>
                </Box>
              ) : activities.length === 0 ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No activities found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your filters or select a different date range
                  </Typography>
                </Box>
              ) : (
                <ActivityTimeline 
                  activities={activities} 
                  loading={loading}
                  loadingMore={loadingMore}
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                />
              )}
            </Box>
            
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      
      <Dialog open={dateDialogOpen} onClose={handleDateDialogClose}>
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate ? startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newDate = e.target.value ? new Date(e.target.value) : null;
                setStartDate(newDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 
                max: endDate ? endDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0] 
              }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate ? endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newDate = e.target.value ? new Date(e.target.value) : null;
                setEndDate(newDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 
                min: startDate ? startDate.toISOString().split('T')[0] : '', 
                max: new Date().toISOString().split('T')[0] 
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
            }} 
            color="error"
          >
            Clear Range
          </Button>
          <Button onClick={handleDateDialogClose}>Cancel</Button>
          <Button 
            onClick={applyDateRange} 
            variant="contained" 
            color="primary"
            disabled={!startDate || !endDate}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Filter Popover */}
      <Popover
        id="filter-menu"
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 300,
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Filter by Activity Type</Typography>
        <FormGroup>
          {Object.keys(filters.activityTypes).map((type) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox 
                  checked={filters.activityTypes[type]} 
                  onChange={handleFilterChange} 
                  name={type} 
                />
              }
              label={type.replace('_', ' ')}
            />
          ))}
        </FormGroup>
        <Divider sx={{ my: 2 }} />
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ color: 'text.secondary' }}>Sort Order</FormLabel>
          <RadioGroup 
            value={filters.sortBy} 
            onChange={handleSortChange}
            name="sort-order"
          >
            <FormControlLabel value="newest" control={<Radio />} label="Newest First" />
            <FormControlLabel value="oldest" control={<Radio />} label="Oldest First" />
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={applyFilters}
          >
            Apply Filters
          </Button>
        </Box>
      </Popover>
    </ThemeProvider>
  );
};

export default ActivityLogs;